import * as DialogPrimitive from '@radix-ui/react-dialog';
import {
  type ButtonHTMLAttributes,
  type ForwardedRef,
  forwardRef,
  type KeyboardEvent,
  type MouseEvent as ReactMouseEvent,
  type ReactNode,
  type RefObject,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import { filterLabeledOptions } from './headless/selection-search.js';
import { Icon } from './icons.js';
import { IconButton } from './primitives.js';
import { useWindowClass } from './window-class.js';

/**
 * Phone pickers (owner decision, W4): at compact width a select or combobox does not open a dropdown
 * under the field (half hidden by the on-screen keyboard inside a sheet). It opens:
 *
 * - a **full-screen picker** (top bar with back and the field label, a search field, the option list)
 *   when the list is long (>= 8 options) or the field searches; the picker is sized to the visual
 *   viewport, so the list ends at the keyboard's top edge and no option hides behind it;
 * - a **bottom sheet** with the options and no text field (so no keyboard) for short lists.
 *
 * Expanded and medium widths keep each field's dropdown menu.
 */

export interface PickerOption {
  readonly description?: ReactNode;
  readonly disabled?: boolean;
  readonly label: string;
  readonly value: string;
}

/** Lists at least this long get the full-screen picker with a search field. */
export const PICKER_FULL_SCREEN_MIN = 8;
/** Lists at least this long focus the search field on open (opens the keyboard). */
export const PICKER_AUTOFOCUS_MIN = 15;

/** True at compact width (phones), where fields open `PickerSheet` instead of a menu. */
export function useCompactPicker(): boolean {
  return useWindowClass('expanded') === 'compact';
}

interface ViewportBox {
  readonly height: number;
  readonly top: number;
}

/**
 * The visible part of the layout viewport (`visualViewport`): with the on-screen keyboard open it is
 * the area above the keyboard. Falls back to the window where the API is missing.
 */
export function useVisualViewportBox(active: boolean): ViewportBox | null {
  const [box, setBox] = useState<ViewportBox | null>(null);
  useEffect(() => {
    if (!active || typeof window === 'undefined') {
      return undefined;
    }
    const viewport = window.visualViewport;
    const update = () =>
      setBox(
        viewport === null || viewport === undefined
          ? { height: window.innerHeight, top: 0 }
          : { height: viewport.height, top: viewport.offsetTop },
      );
    update();
    viewport?.addEventListener('resize', update);
    viewport?.addEventListener('scroll', update);
    window.addEventListener('resize', update);
    return () => {
      viewport?.removeEventListener('resize', update);
      viewport?.removeEventListener('scroll', update);
      window.removeEventListener('resize', update);
    };
  }, [active]);
  return active ? box : null;
}

export interface PickerSearch {
  /** Focus the field on open (searchable lists); otherwise the keyboard stays closed until a tap. */
  readonly autoFocus?: boolean;
  readonly loading?: boolean;
  readonly onChange: (query: string) => void;
  readonly placeholder?: string;
  readonly value: string;
}

export interface PickerSheetProps {
  readonly emptyMessage?: string;
  /** Several values (checkbox rows; a tap toggles and the picker stays open). */
  readonly multiple?: boolean;
  readonly onOpenChange: (open: boolean) => void;
  readonly onPick: (value: string) => void;
  readonly open: boolean;
  readonly options: readonly PickerOption[];
  /** The caller's own search (combobox): the picker shows `options` as they are. */
  readonly search?: PickerSearch;
  /** Gets focus back when the picker closes (the field's trigger). */
  readonly returnFocusRef?: RefObject<HTMLElement | null>;
  readonly selected: readonly string[];
  readonly title: ReactNode;
}

/** The full-screen or bottom-sheet option picker of compact widths (see the module comment). */
export function PickerSheet({
  emptyMessage = 'نتیجه‌ای نیست',
  multiple = false,
  onOpenChange,
  onPick,
  open,
  options,
  returnFocusRef,
  search,
  selected,
  title,
}: PickerSheetProps) {
  const fullScreen = search !== undefined || options.length >= PICKER_FULL_SCREEN_MIN;
  const [ownQuery, setOwnQuery] = useState('');
  useEffect(() => {
    if (!open) setOwnQuery('');
  }, [open]);
  const query = search?.value ?? ownQuery;
  const shown = useMemo(
    () => (search === undefined ? filterLabeledOptions(options, ownQuery) : options),
    [options, ownQuery, search],
  );
  const autoFocus = search?.autoFocus ?? options.length >= PICKER_AUTOFOCUS_MIN;
  const box = useVisualViewportBox(open && fullScreen);
  const searchRef = useRef<HTMLInputElement | null>(null);
  const listRef = useRef<HTMLDivElement | null>(null);

  function pick(value: string): void {
    onPick(value);
    if (!multiple) onOpenChange(false);
  }

  function onSearchKeyDown(event: KeyboardEvent<HTMLInputElement>): void {
    const enabled = shown.filter((option) => option.disabled !== true);
    if (event.key === 'Enter' && enabled.length === 1 && enabled[0] !== undefined) {
      event.preventDefault();
      pick(enabled[0].value);
    }
  }

  return (
    <DialogPrimitive.Root onOpenChange={onOpenChange} open={open}>
      <DialogPrimitive.Portal>
        {fullScreen ? null : (
          <DialogPrimitive.Overlay className="saay-overlay saay-picker__scrim" />
        )}
        <DialogPrimitive.Content
          aria-describedby={undefined}
          className="saay-picker"
          data-mode={fullScreen ? 'full' : 'sheet'}
          dir="rtl"
          onCloseAutoFocus={(event) => {
            const target = returnFocusRef?.current;
            if (target !== null && target !== undefined) {
              event.preventDefault();
              target.focus();
            }
          }}
          onOpenAutoFocus={(event) => {
            event.preventDefault();
            if (fullScreen && autoFocus) {
              searchRef.current?.focus();
              return;
            }
            // No keyboard: focus the selected row (or the first) and keep it in view.
            const list = listRef.current;
            const target =
              list?.querySelector<HTMLElement>('[aria-selected="true"]') ??
              list?.querySelector<HTMLElement>('[role="option"]:not([disabled])');
            target?.focus({ preventScroll: true });
            target?.scrollIntoView({ block: 'nearest' });
          }}
          style={
            fullScreen && box !== null
              ? { blockSize: `${box.height}px`, insetBlockStart: `${box.top}px` }
              : undefined
          }
        >
          {fullScreen ? (
            <div className="saay-picker__bar">
              <IconButton
                icon="arrow-left"
                label="بازگشت"
                onClick={() => onOpenChange(false)}
                type="button"
              />
              <DialogPrimitive.Title className="saay-picker__title">{title}</DialogPrimitive.Title>
            </div>
          ) : (
            <>
              <div aria-hidden="true" className="saay-picker__handle" />
              <DialogPrimitive.Title className="saay-picker__title">{title}</DialogPrimitive.Title>
            </>
          )}
          {fullScreen ? (
            <div className="saay-picker__search">
              <Icon name={search?.loading === true ? 'refresh' : 'search'} />
              <input
                aria-label={search?.placeholder ?? 'جست‌وجو'}
                autoComplete="off"
                enterKeyHint="search"
                onChange={(event) =>
                  search === undefined
                    ? setOwnQuery(event.target.value)
                    : search.onChange(event.target.value)
                }
                onKeyDown={onSearchKeyDown}
                placeholder={search?.placeholder ?? 'جست‌وجو'}
                ref={searchRef}
                type="search"
                value={query}
              />
            </div>
          ) : null}
          <div
            aria-busy={search?.loading === true || undefined}
            aria-multiselectable={multiple || undefined}
            className="saay-picker__list"
            ref={listRef}
            role="listbox"
          >
            {shown.length === 0 ? (
              <p className="saay-picker__empty" role="status">
                {search?.loading === true ? 'در حال جست‌وجو…' : emptyMessage}
              </p>
            ) : (
              shown.map((option) => {
                const isSelected = selected.includes(option.value);
                return (
                  <button
                    aria-selected={isSelected}
                    className="saay-picker__option"
                    disabled={option.disabled === true}
                    key={option.value}
                    onClick={() => pick(option.value)}
                    role="option"
                    type="button"
                  >
                    {multiple ? (
                      <span
                        aria-hidden="true"
                        className="saay-picker__check"
                        data-checked={isSelected || undefined}
                      >
                        {isSelected ? (
                          <svg focusable="false" viewBox="0 0 18 18">
                            <path d="M3.75 9.125 7.25 12.625 14.25 5.375" />
                          </svg>
                        ) : null}
                      </span>
                    ) : null}
                    <span className="saay-picker__option-text">
                      <span>{option.label}</span>
                      {option.description === undefined ? null : (
                        <span className="saay-picker__option-desc">{option.description}</span>
                      )}
                    </span>
                    {!multiple && isSelected ? <Icon name="check" /> : null}
                  </button>
                );
              })
            )}
          </div>
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  );
}

/**
 * A press on a picker trigger does not take focus from the field being edited: that field's on-blur
 * validation would otherwise insert an error line and move the trigger before the click lands.
 * Opening the picker moves focus anyway.
 */
function keepFocusOnPress(event: ReactMouseEvent<HTMLButtonElement>): void {
  event.preventDefault();
}

/** The compact field trigger: looks like the field, opens the picker (no keyboard). */
export const PickerTrigger = forwardRef<
  HTMLButtonElement,
  ButtonHTMLAttributes<HTMLButtonElement> & {
    readonly children: ReactNode;
    readonly invalid?: boolean;
  }
>(function PickerTrigger({ className, children, invalid, ...rest }, ref) {
  return (
    <button
      onMouseDown={keepFocusOnPress}
      {...rest}
      aria-haspopup="dialog"
      className={['saay-picker-trigger', className].filter(Boolean).join(' ')}
      data-invalid={invalid || undefined}
      ref={ref}
      type="button"
    >
      <span className="saay-picker-trigger__value">{children}</span>
      <Icon name="chevron-down" />
    </button>
  );
});

/** The options of a native `<select>` (visible ones; a hidden placeholder is left out). */
export function nativeSelectOptions(
  select: HTMLSelectElement,
  includeEmpty = true,
): readonly PickerOption[] {
  return Array.from(select.options)
    .filter((option) => !option.hidden && (includeEmpty || option.value !== ''))
    .map((option) => ({
      disabled: option.disabled,
      label: option.label || option.text,
      value: option.value,
    }));
}

/** Sets a native select's value as a user choice would (React `onChange` and form libraries see it). */
export function chooseNativeSelectValue(select: HTMLSelectElement, value: string): void {
  if (select.value === value) return;
  select.value = value;
  select.dispatchEvent(new Event('input', { bubbles: true }));
  select.dispatchEvent(new Event('change', { bubbles: true }));
}

/**
 * A native `<select>` on a phone: an invisible button over the select opens `PickerSheet` with the
 * select's own options, and the choice is written back to the select (so `register`, controlled
 * `value`, and form submission keep working). The select still shows the chosen text.
 */
export function NativeSelectPicker({
  ariaLabel,
  describedBy,
  disabled,
  id,
  includeEmpty = false,
  label,
  selectRef,
}: {
  /** For a select without a visible label (toolbar filters): the button's name. */
  readonly ariaLabel?: string;
  readonly describedBy?: string | undefined;
  readonly disabled?: boolean | undefined;
  readonly id: string;
  /**
   * Offer the empty-value option. A form placeholder («یک شعبه انتخاب کنید») is not a choice; a
   * filter's «همه …» is.
   */
  readonly includeEmpty?: boolean;
  readonly label: ReactNode;
  readonly selectRef: RefObject<HTMLSelectElement | null>;
}) {
  const [open, setOpen] = useState(false);
  const [options, setOptions] = useState<readonly PickerOption[]>([]);
  const [value, setValue] = useState('');
  const buttonRef = useRef<HTMLButtonElement | null>(null);
  const [text, setText] = useState('');

  // The button's accessible value follows the select (it may change from a form reset).
  useEffect(() => {
    const select = selectRef.current;
    if (select === null) return undefined;
    const sync = () => setText(select.selectedOptions[0]?.text ?? '');
    // Code that focuses the select (next field, first invalid field) lands on the button.
    const forward = () => buttonRef.current?.focus();
    sync();
    select.addEventListener('change', sync);
    select.addEventListener('focus', forward);
    return () => {
      select.removeEventListener('change', sync);
      select.removeEventListener('focus', forward);
    };
  });

  return (
    <>
      <button
        aria-describedby={describedBy}
        aria-haspopup="dialog"
        {...(ariaLabel === undefined ? {} : { 'aria-label': `${ariaLabel}: ${text}` })}
        className="saay-native-picker__button"
        disabled={disabled}
        onMouseDown={keepFocusOnPress}
        id={id}
        onClick={() => {
          const select = selectRef.current;
          if (select === null) return;
          setOptions(nativeSelectOptions(select, includeEmpty));
          setValue(select.value);
          setOpen(true);
        }}
        ref={buttonRef}
        type="button"
      >
        <span className="saay-visually-hidden">{text}</span>
      </button>
      <PickerSheet
        onOpenChange={setOpen}
        onPick={(next) => {
          const select = selectRef.current;
          if (select !== null) chooseNativeSelectValue(select, next);
        }}
        open={open}
        options={options}
        returnFocusRef={buttonRef}
        selected={[value]}
        title={label}
      />
    </>
  );
}

/** Writes `node` to a forwarded ref (callback or object). */
export function assignRef<T>(ref: ForwardedRef<T>, node: T | null): void {
  if (typeof ref === 'function') {
    ref(node);
  } else if (ref !== null) {
    ref.current = node;
  }
}
